import { useEffect, useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

import type { PropertyData } from '@/domain/entities/property-object';
import { PropertyTab } from './PropertyTab';
import { LocationsTab } from './LocationsTab';
import { UtilitiesTab } from './UtilitiesTab';
import { SystemsTab } from './SystemsTab';
import { DocumentsTab } from './DocumentsTab';
import { MembersTab } from './MembersTab';
import type { PropertyEditorTabKey } from '../propertyEditorTypes';
import { EventsTab } from './EventsTab';
import { usePermissions } from '@/hooks/usePermissions';
import { Permissions } from '@/lib/permissions';

type BuilderMode = 'create' | 'edit';

export function PropertyEditorTabs(props: {
  data: PropertyData;
  setData: (next: PropertyData) => void;

  // ✅ new
  mode: BuilderMode;
  propertyIdFromRoute?: string;
}) {
  const { data, setData, mode, propertyIdFromRoute } = props;
  const { hasPermission, isSuperAdmin } = usePermissions();

  // ✅ controlled tab state so we can react to tab switches later (fetch on enter, etc.)
  const [activeTab, setActiveTab] = useState<PropertyEditorTabKey>('property');
  const showLocations = mode === 'create' || isSuperAdmin || hasPermission(Permissions.LocationsRead);
  const showUtilities = mode === 'create' || isSuperAdmin || hasPermission(Permissions.UtilitiesRead);
  const showSystems = mode === 'create' || isSuperAdmin || hasPermission(Permissions.SystemsRead);
  const showEvents = mode !== 'create' && (isSuperAdmin || hasPermission(Permissions.PropertiesRead));
  const showDocuments = mode !== 'create' && (isSuperAdmin || hasPermission(Permissions.DocumentsRead));
  const showMembers = mode !== 'create' && (isSuperAdmin || hasPermission(Permissions.MemberRead));

  // Optional: when property changes (new route), reset to Property tab
  useEffect(() => {
    setActiveTab('property');
  }, [propertyIdFromRoute]);

  const propertyId = data.property?.id ?? propertyIdFromRoute;
  if (mode === 'edit' && !propertyId) {
    throw new Error('PropertyEditorTabs: propertyIdFromRoute is required in edit mode');
  }

  return (
    <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as typeof activeTab)} className="space-y-6">
      <TabsList>
        <TabsTrigger value="property">Property</TabsTrigger>
        {showLocations ? <TabsTrigger value="locations">Locations</TabsTrigger> : null}
        {showUtilities ? <TabsTrigger value="utilities">Utilities</TabsTrigger> : null}
        {showSystems ? <TabsTrigger value="systems">Systems</TabsTrigger> : null}
        {showDocuments ? <TabsTrigger value="documents">Documents</TabsTrigger> : null}
        {showMembers ? <TabsTrigger value="members">Members</TabsTrigger> : null}
        {showEvents ? <TabsTrigger value="events">Events</TabsTrigger> : null}
      </TabsList>

      <TabsContent value="property">
        <PropertyTab data={data} setData={setData} mode={mode} propertyIdFromRoute={propertyIdFromRoute} />
      </TabsContent>

      {showLocations ? (
        <TabsContent value="locations">
          <LocationsTab data={data} setData={setData} mode={mode} propertyId={propertyId!} />
        </TabsContent>
      ) : null}

      {showUtilities ? (
        <TabsContent value="utilities">
          <UtilitiesTab data={data} setData={setData} mode={mode} propertyId={propertyId!} />
        </TabsContent>
      ) : null}

      {showSystems ? (
        <TabsContent value="systems">
          <SystemsTab data={data} setData={setData} mode={mode} propertyId={propertyId!} />
        </TabsContent>
      ) : null}

      {showDocuments ? (
        <TabsContent value="documents">
          <DocumentsTab propertyId={propertyId!} />
        </TabsContent>
      ) : null}

      {showMembers ? (
        <TabsContent value="members">
          <MembersTab propertyId={propertyId!} />
        </TabsContent>
      ) : null}

      {showEvents ? (
        <TabsContent value="events">
          <EventsTab propertyId={propertyId!} />
        </TabsContent>
      ) : null}
    </Tabs>
  );
}
